import { logError, logInfo } from "./logger";

/**
 * Options for the retry function
 */
export interface RetryOptions {
  /**
   * Maximum number of retry attempts
   * @default 3
   */
  retries?: number;

  /**
   * Delay between retry attempts in milliseconds
   * @default 300
   */
  delayMs?: number;

  /**
   * Whether to use exponential backoff for retry delays
   * @default false
   */
  useExponentialBackoff?: boolean;

  /**
   * Optional callback function to execute before each retry
   */
  onRetry?: (attempt: number, error: Error) => void;

  /**
   * Optional error filter function to determine if retry should be attempted for a specific error
   * @returns true if the error should trigger a retry, false otherwise
   */
  retryCondition?: (error: Error) => boolean;
}

/**
 * Retries a function execution with configurable options
 * @param fn The function to retry
 * @param options Retry configuration options
 * @returns A promise that resolves with the result of the function or rejects after all retries fail
 */
export const retry = async <T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> => {
  const {
    retries = 3,
    delayMs = 300,
    useExponentialBackoff = false,
    onRetry,
    retryCondition = () => true,
  } = options;

  let attempt = 0;

  const execute = async (): Promise<T> => {
    try {
      return await fn();
    } catch (error) {
      attempt++;

      // Check if we've exceeded max retries or if the error doesn't meet retry condition
      if (attempt > retries || !retryCondition(error as Error)) {
        logError(`Retry failed after ${attempt} attempts:`, error as Error);
        throw error;
      }

      // Calculate delay (with exponential backoff if enabled)
      const nextDelay = useExponentialBackoff
        ? delayMs * Math.pow(2, attempt - 1)
        : delayMs;

      // Execute onRetry callback if provided
      if (onRetry) {
        onRetry(attempt, error as Error);
      }

      logInfo(`Retry attempt ${attempt}/${retries} after ${nextDelay}ms`);

      // Wait for the delay
      await new Promise((resolve) => setTimeout(resolve, nextDelay));

      // Try again
      return execute();
    }
  };

  return execute();
};
